import { Card } from '@/components/common/card'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import {
  ActivePositionsChart,
  type BreakdownTab,
  DEFAULT_BREAKDOWN_TAB,
} from '@/components/dashboard/activePositionsChart'
import { ActivePositionsContent, ActivePositionsPanel } from '@/components/dashboard/activePositionsPanel'
import { BorrowPositionsContent, BorrowPositionsPanel } from '@/components/dashboard/borrowPositionsPanel'
import { IdleAssetsContent, IdleAssetsHeader } from '@/components/dashboard/idleAssetsPanel'
import { ProposedVaultsPanel } from '@/components/dashboard/proposedVaultsPanel'
import { Content, Page } from '@/components/layout/wrappers'
import { DASHBOARD_BREAKDOWN_TAB_COOKIE_KEY } from '@/constants/cookie'
import { useAccountContext } from '@/context/account'
import { assetsBalanceMock } from '@/mocks/dashboard/assetsBalance.mock'
import { lpTokensBalanceMock } from '@/mocks/dashboard/lpTokensBalance.mock'
import type { BorrowPosition } from '@/queries/borrowPositions.query'
import type { Rewards } from '@/queries/rewards.query'
import type { VaultApyRange } from '@/types/apy'
import { cn } from '@/utils/cn'
import { setCookieValue } from '@/utils/cookie/cookie'
import { networkNameSchema, vaultIdSchema } from '@vaultsfyi/common/schemas'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { type Address, isAddressEqual } from 'viem'
import { z } from 'zod'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../common/accordion'
import { BlurCover } from '../common/blurCover'
import { Button } from '../common/button'
import { Separator } from '../common/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../common/tabs'
import { ArrowIcon } from '../icons/arrow'
import { RewardsPanelContent, RewardsPanelFooter, RewardsPanelHeader } from './rewardsPanel'
import { TrackPortfolioDialog } from './trackPortfolioDialog'
import { TrackedAddressEditor } from './trackedAddressEditor'
import type { AssetsBalanceGroup } from './useGroupedBalances/useGroupedBalances'
import type { LpTokensBalanceGroup } from './useGroupedBalances/useGroupedPositions'

interface DashboardPageProps {
  address: Address | undefined
  editableAddress?: boolean
  includeRewards: boolean
  apyRange: VaultApyRange
  lpTokensBalance: LpTokensBalanceGroup | null | undefined
  assetsBalance: AssetsBalanceGroup | null | undefined
  borrowPositions: BorrowPosition[] | null | undefined
  rewards: Rewards | undefined
  hideFunding?: boolean
  initialBreakdownTab?: BreakdownTab
}

const DASHBOARD_TABS = {
  activePositions: 'Active positions',
  idleAssets: 'Idle assets',
} as const

const REWARDS_TAB = 'rewards'

const highlightSchema = z.object({
  vaultId: vaultIdSchema,
  network: networkNameSchema,
  symbol: z.string(),
})

export function DashboardPage({
  address,
  editableAddress,
  includeRewards,
  apyRange,
  lpTokensBalance,
  assetsBalance,
  borrowPositions,
  rewards,
  hideFunding,
  initialBreakdownTab,
}: DashboardPageProps) {
  const location = useLocation()
  const { account } = useAccountContext()
  const defaultTab =
    lpTokensBalance && lpTokensBalance.length > 0 ? DASHBOARD_TABS.activePositions : DASHBOARD_TABS.idleAssets
  const [dashboardTab, setDashboardTab] = useState(defaultTab as string)
  const [rewardsOpen, setRewardsOpen] = useState('')
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>(initialBreakdownTab ?? DEFAULT_BREAKDOWN_TAB)

  const handleBreakdownTabChange = useCallback((tab: BreakdownTab) => {
    setBreakdownTab(tab)
    setCookieValue(DASHBOARD_BREAKDOWN_TAB_COOKIE_KEY, tab)
  }, [])

  const sameTrackedAddress = address && account && isAddressEqual(address, account)
  const highlightState = highlightSchema.safeParse(location.state)
  const highlightParams = highlightState.success ? highlightState.data : null

  const displayableLpTokensBalance = lpTokensBalance ?? lpTokensBalanceMock
  const displayableAssetsBalance = assetsBalance ?? assetsBalanceMock
  const showActivePositions = !lpTokensBalance || lpTokensBalance.length > 0
  const showBorrowPositions = borrowPositions !== null && borrowPositions !== undefined && borrowPositions.length > 0

  useEffect(() => {
    if (highlightParams) {
      const positionElements = Array.from(
        document.body.querySelectorAll(`[data-highlight="${highlightParams.network}:${highlightParams.vaultId}"]`)
      )
      const assetElements = Array.from(document.body.querySelectorAll(`[data-highlight="${highlightParams.symbol}"]`))
      const positionElement = positionElements.find((item) => item.checkVisibility({ contentVisibilityAuto: true }))
      const assetElement = assetElements.find((item) => item.checkVisibility({ contentVisibilityAuto: true }))

      if (positionElement) {
        setDashboardTab(DASHBOARD_TABS.activePositions)
        positionElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        positionElement.classList.add('animate-glow-frame', 'repeat-[4]')
      }
      if (assetElement) {
        assetElement.classList.add('animate-glow-frame', 'repeat-[4]')
      }
    }
  }, [location])

  return (
    <Page className="relative">
      {!address && !account && <TrackPortfolioDialog address={undefined} />}
      <ScrollArea className="h-full">
        <Tabs className="lg:hidden" value={dashboardTab} onValueChange={(tab) => setDashboardTab(tab)}>
          {address && editableAddress && !sameTrackedAddress && (
            <TrackedAddressEditor address={address} className="m-4" />
          )}
          <TabsList variant="line" className="sticky top-0 z-[2] w-full bg-page">
            {Object.entries(DASHBOARD_TABS).map(([key, title]) => (
              <TabsTrigger key={key} variant="line" value={title}>
                {title}
              </TabsTrigger>
            ))}
            {rewards && (
              <TabsTrigger variant="line" value={REWARDS_TAB}>
                Rewards
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value={DASHBOARD_TABS.activePositions}>
            {showActivePositions && (
              <div className={cn('relative', !lpTokensBalance && 'animate-pulse')}>
                <div className="px-4 pt-4 md:px-5">
                  <ActivePositionsChart
                    lpTokensBalance={displayableLpTokensBalance}
                    apyRange={apyRange}
                    breakdownTab={breakdownTab}
                    onBreakdownTabChange={handleBreakdownTabChange}
                  />
                </div>
                <div className="flex flex-col py-4 md:pb-5">
                  <ActivePositionsContent
                    walletAddress={address}
                    lpTokensBalance={displayableLpTokensBalance}
                    includeRewards={includeRewards}
                    apyRange={apyRange}
                    breakdownTab={breakdownTab}
                    className="flex flex-col"
                  />
                </div>
                {!lpTokensBalance && <BlurCover className="bg-transparent" />}
              </div>
            )}
            {showBorrowPositions && <BorrowPositionsContent borrowPositions={borrowPositions} />}
            <ProposedVaultsPanel
              address={address}
              apyRange={apyRange}
              includeRewards={includeRewards}
              assetsBalance={assetsBalance}
              className="px-4 pb-4 md:px-5 md:pb-5"
            />
          </TabsContent>
          <TabsContent value={DASHBOARD_TABS.idleAssets} className={cn(!assetsBalance && '[&>*]:animate-pulse')}>
            <IdleAssetsHeader assetsBalance={displayableAssetsBalance} hideFunding={hideFunding} className="py-4" />
            <IdleAssetsContent assetsBalance={displayableAssetsBalance} className="pb-4 md:pb-5" />
            {!assetsBalance && <BlurCover className="bg-transparent" />}
          </TabsContent>
          {rewards && (
            <TabsContent value={REWARDS_TAB}>
              <RewardsPanelHeader rewards={rewards} className="py-4" />
              <RewardsPanelContent rewards={rewards} className="pb-3" />
              {sameTrackedAddress && <RewardsPanelFooter rewards={rewards} />}
            </TabsContent>
          )}
        </Tabs>
        <Content className="hidden lg:block">
          <div className="grid grid-cols-[1fr_380px] gap-5">
            {address && editableAddress && !sameTrackedAddress && (
              <TrackedAddressEditor address={address} className="col-span-2" />
            )}
            <div className="relative flex max-w-full flex-col gap-5 overflow-hidden">
              {showActivePositions && (
                <>
                  <ActivePositionsChart
                    lpTokensBalance={displayableLpTokensBalance}
                    apyRange={apyRange}
                    breakdownTab={breakdownTab}
                    onBreakdownTabChange={handleBreakdownTabChange}
                    loading={!lpTokensBalance}
                  />
                  <ActivePositionsPanel
                    lpTokensBalance={displayableLpTokensBalance}
                    walletAddress={address}
                    includeRewards={includeRewards}
                    apyRange={apyRange}
                    breakdownTab={breakdownTab}
                    loading={!lpTokensBalance}
                  />
                </>
              )}
              {showBorrowPositions && <BorrowPositionsPanel borrowPositions={borrowPositions} />}
              <ProposedVaultsPanel
                address={address}
                apyRange={apyRange}
                includeRewards={includeRewards}
                assetsBalance={assetsBalance}
              />
            </div>
            <div className="sticky top-6 flex h-fit flex-col gap-5">
              {rewards && (
                <Card className={cn('relative bg-surface p-0 pt-4 lg:pt-5')}>
                  <Accordion
                    type="single"
                    collapsible
                    value={rewardsOpen}
                    onValueChange={setRewardsOpen}
                    className="w-full"
                  >
                    <AccordionItem value="rewards">
                      <RewardsPanelHeader rewards={rewards} />
                      <div className="mt-2 px-4 lg:px-5">
                        <Separator />
                      </div>
                      <AccordionContent>
                        <ScrollArea type="auto" className="flex max-h-72 flex-col overflow-y-auto">
                          <RewardsPanelContent rewards={rewards} className="pt-2 pb-4 lg:pb-5" />
                          <ScrollBar />
                        </ScrollArea>
                        {sameTrackedAddress && <RewardsPanelFooter rewards={rewards} className="pb-2 md:pb-2" />}
                      </AccordionContent>
                      <AccordionTrigger asChild>
                        <Button variant="default" size="default" className="group w-full gap-2 px-5 py-2">
                          {rewardsOpen ? 'Hide rewards' : 'Show rewards'}
                          <div className="group-data-[state=open]:-scale-y-100 transition-transform">
                            <ArrowIcon direction="down" />
                          </div>
                        </Button>
                      </AccordionTrigger>
                    </AccordionItem>
                  </Accordion>
                </Card>
              )}
              <Card
                as="aside"
                className={cn('relative bg-surface p-0 pt-4 lg:pt-5', !assetsBalance && '[&>*]:animate-pulse')}
                data-role="side-panel"
              >
                <IdleAssetsHeader assetsBalance={displayableAssetsBalance} hideFunding={hideFunding} />
                <div className="mt-2 px-4 lg:px-5">
                  <Separator />
                </div>
                <ScrollArea type="auto" className="flex max-h-96 flex-col overflow-y-auto">
                  <IdleAssetsContent assetsBalance={displayableAssetsBalance} className="pt-2 pb-4 lg:pb-5" />
                  <ScrollBar />
                </ScrollArea>
                {!assetsBalance && <BlurCover className="bg-transparent" />}
              </Card>
            </div>
          </div>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
